/**
 * MobileMenuDrawer — Phase 2c (vibe-designer 1.126.0).
 *
 * Left-edge slide-in drawer for TopBar overflow controls hidden on touch.
 * Contains: View Mode toggle (Designer/Terminals), Tenant chip, Deploy
 * button, Sign Out button.
 *
 * Visibility gated on uiStore.mobileMenuOpen. Touch UAs open via the
 * hamburger button in TopBar; desktop UAs never see it (the hamburger is
 * gated on [@media(pointer:coarse)]:flex + default `hidden`, so on desktop
 * mobileMenuOpen can never flip to true via UI — but the conditional
 * render here is defensive against programmatic flips).
 *
 * Slide-in animation: animate-slide-in-left from tailwind.config.js
 * (added in this commit; mirror of slide-in-right used by MindChatWidget).
 *
 * Z-index layering (reviewer-G0 [C1] fix to avoid collision with
 * MindChatWidget panel at z-[10000]):
 *   • MindChatWidget FAB:        removed 1.555.0 (trigger moved to TopBar)
 *   • MindChatWidget backdrop:   z-[9999]
 *   • MindChatWidget panel:      z-[10000]
 *   • MindChatWidget resize:     z-[10001]
 *   • MobileMenuDrawer backdrop: z-[10002] (this file)
 *   • MobileMenuDrawer panel:    z-[10003] (this file)
 * Drawer takes over above MIND panel if both happen to be open simultaneously.
 *
 * Escape-key support is deferred to Phase 2c.1 (low-priority polish; the
 * backdrop click + close button cover the primary close paths).
 */
import { useEffect } from 'react';
import {
  Cloud,
  Upload,
  LogOut,
  Code,
  Terminal as TerminalIcon,
  Loader2,
  Menu as MenuIcon,
  BookOpen,
  LayoutDashboard,
} from 'lucide-react';
import clsx from 'clsx';
import { useUIStore } from '@/stores/uiStore';
import { useTenantStore } from '@/stores/tenantStore';
import { useDeployStore } from '@/stores/deployStore';
import { useProjectStore } from '@/stores/projectStore';
import { useAuth } from '@/contexts/useAuth';

export function MobileMenuDrawer() {
  const open = useUIStore((s) => s.mobileMenuOpen);
  const setMobileMenuOpen = useUIStore((s) => s.setMobileMenuOpen);
  const viewMode = useUIStore((s) => s.viewMode);
  const setViewMode = useUIStore((s) => s.setViewMode);
  const tenantId = useTenantStore((s) => s.tenantId);
  const deploymentStatus = useDeployStore((s) => s.deploymentStatus);
  const deployProject = useDeployStore((s) => s.deployProject);
  const pollDeployStatus = useDeployStore((s) => s.pollDeployStatus);
  const currentProject = useProjectStore((s) => s.currentProject);
  const { logout } = useAuth();

  // 1.129.0 Phase 2f: Escape-key closes the drawer. Adapted from
  // MindChatWidget.tsx:189-196 — MindChatWidget is always mounted so its
  // listener is always active; this drawer is unmount-on-close so we
  // gate inside the effect body via `if (!open) return`. Effect re-runs
  // when `open` flips, attaching/detaching the listener appropriately.
  useEffect(() => {
    if (!open) return;
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKeydown);
    return () => document.removeEventListener('keydown', onKeydown);
  }, [open, setMobileMenuOpen]);

  if (!open) return null;

  const isDeployingNow =
    deploymentStatus.status === 'syncing' ||
    deploymentStatus.status === 'applying-nginx';

  const handleDeploy = async () => {
    if (!currentProject) return;
    setMobileMenuOpen(false);
    await deployProject(currentProject.id);
    void pollDeployStatus(currentProject.id);
  };

  // 1.168.0 Phase 1: extended to 3 modes (designer | knowledge | terminals).
  // Dashboard tab P1 (2026-07-28): 'dashboard' added — the friendly onboarding
  // surface must be reachable from the phone drawer too.
  const handleViewModeClick = (mode: 'dashboard' | 'designer' | 'knowledge' | 'terminals') => {
    setViewMode(mode);
    setMobileMenuOpen(false);
  };

  const handleSignOut = () => {
    setMobileMenuOpen(false);
    logout();
  };

  return (
    <div
      data-testid="mobile-menu-drawer-backdrop"
      onClick={() => setMobileMenuOpen(false)}
      className="fixed inset-0 z-[10002] bg-black/40"
    >
      <div
        data-testid="mobile-menu-drawer"
        onClick={(e) => e.stopPropagation()}
        className="fixed top-0 left-0 h-full w-72 max-w-[80vw] bg-vibe-bg-secondary border-r border-vibe-border shadow-lg z-[10003] animate-slide-in-left flex flex-col"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-vibe-border">
          <div className="flex items-center gap-2 text-vibe-text">
            <MenuIcon className="w-4 h-4" />
            <span className="font-semibold">Menu</span>
          </div>
          <button
            data-testid="mobile-menu-drawer-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
            className="text-xs text-vibe-text-secondary hover:text-vibe-text [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <div className="text-xs text-vibe-text-secondary uppercase mb-2">View</div>
            <div
              role="group"
              aria-label="View mode"
              className="flex items-center bg-vibe-bg-tertiary rounded p-0.5"
            >
              {/* Dashboard tab P1 (§11 LOCKED 2026-07-28) — leftmost/friendly
                  onboarding surface, reachable on phone too. */}
              <button
                data-testid="mobile-menu-view-dashboard"
                aria-pressed={viewMode === 'dashboard'}
                onClick={() => handleViewModeClick('dashboard')}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded transition-colors',
                  '[@media(pointer:coarse)]:min-h-[44px]',
                  viewMode === 'dashboard'
                    ? 'bg-vibe-accent text-white'
                    : 'text-vibe-text-secondary hover:text-vibe-text',
                )}
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </button>
              <button
                data-testid="mobile-menu-view-designer"
                aria-pressed={viewMode === 'designer'}
                onClick={() => handleViewModeClick('designer')}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded transition-colors',
                  '[@media(pointer:coarse)]:min-h-[44px]',
                  viewMode === 'designer'
                    ? 'bg-vibe-accent text-white'
                    : 'text-vibe-text-secondary hover:text-vibe-text',
                )}
              >
                <Code className="w-4 h-4" />
                Designer
              </button>
              {/* 1.168.0 Phase 1 — 3rd view-mode option "Knowledge". */}
              <button
                data-testid="mobile-menu-view-knowledge"
                aria-pressed={viewMode === 'knowledge'}
                onClick={() => handleViewModeClick('knowledge')}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded transition-colors',
                  '[@media(pointer:coarse)]:min-h-[44px]',
                  viewMode === 'knowledge'
                    ? 'bg-vibe-accent text-white'
                    : 'text-vibe-text-secondary hover:text-vibe-text',
                )}
              >
                <BookOpen className="w-4 h-4" />
                Knowledge
              </button>
              <button
                data-testid="mobile-menu-view-terminals"
                aria-pressed={viewMode === 'terminals'}
                onClick={() => handleViewModeClick('terminals')}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded transition-colors',
                  '[@media(pointer:coarse)]:min-h-[44px]',
                  viewMode === 'terminals'
                    ? 'bg-vibe-accent text-white'
                    : 'text-vibe-text-secondary hover:text-vibe-text',
                )}
              >
                <TerminalIcon className="w-4 h-4" />
                Terminals
              </button>
            </div>
          </div>

          <div>
            <div className="text-xs text-vibe-text-secondary uppercase mb-2">Tenant</div>
            <div
              data-testid="mobile-menu-tenant"
              className="flex items-center gap-2 px-3 py-2 text-sm bg-vibe-bg-tertiary rounded"
            >
              <Cloud className="w-4 h-4 text-vibe-text-secondary" />
              {/* 1.212.0 fallback changed 'no tenant'→'…' (loading) per R3 LIVE 2026-05-27. */}
              <span className="text-vibe-text">{tenantId ?? '…'}</span>
            </div>
          </div>

          <div>
            <div className="text-xs text-vibe-text-secondary uppercase mb-2">Deploy</div>
            <button
              data-testid="mobile-menu-deploy-button"
              onClick={() => void handleDeploy()}
              disabled={!currentProject || isDeployingNow}
              className={clsx(
                'w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded transition-colors',
                '[@media(pointer:coarse)]:min-h-[44px]',
                'bg-vibe-accent hover:bg-vibe-accent-hover text-white',
                (!currentProject || isDeployingNow) && 'opacity-60 cursor-not-allowed',
              )}
            >
              {isDeployingNow ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deploying...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Deploy
                </>
              )}
            </button>
          </div>
        </div>

        <div className="p-4 border-t border-vibe-border">
          <button
            data-testid="mobile-menu-sign-out"
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded text-vibe-error hover:bg-vibe-error/10 transition-colors [@media(pointer:coarse)]:min-h-[44px]"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

export default MobileMenuDrawer;
